/*
For now it just fetches the assets so that they are cached by the browser.
Something more drastic could be done by actually caching the binary data in memory, but I'm not sure it's worth it for simple games.
*/

import { useEffect } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import * as Sentry from '@sentry/nextjs';
import { dedupSimple } from '@/modules/arrayUtils';
import sendEvent from '@/modules/monitoring/sendEvent';
import { truncateObjectString } from '@/modules/stringUtils';

export default function useGameAssetsContext({
  gameAssetsFolder,
  gameData,
  prepareAssets,
}) {
  const { state, actions } = useCustomHook({
    defaultState: {
      sounds: null,
      images: null,
      sprites: null,
      isReady: false,
      error: false,
    },
  });

  useEffect(() => {
    if (gameData && gameAssetsFolder && !state.isReady) {
      if (prepareAssets) {
        async function loadAssets() {
          try {
            //console.log('LOADING ASSETS...');
            let { soundsData, imagesData, spritesData } = await prepareAssets();
            //console.log({ soundsData, imagesData, spritesData });

            try {
              if (spritesData) {
                spritesData = await preloadSprites(spritesData);
              }
              if (soundsData) {
                soundsData = await preloadAudios(soundsData);
              }
              if (imagesData) {
                imagesData = await preloadImages(imagesData);
              }

              //console.log('Assets loaded');
            } catch (error) {
              console.error('Error loading assets:', error);
              Sentry.captureException(error, {
                extra: {
                  soundsData,
                  imagesData,
                  spritesData,
                },
              });
            }
            actions.setState({
              sounds: soundsData,
              images: imagesData,
              sprites: spritesData,
              isReady: true,
              error: false,
            });
          } catch (error) {
            console.error('Error preparing assets:', error);
            actions.setState({
              error: true,
            });
            Sentry.captureException(error, {
              extra: {
                gameData,
                gameAssetsFolder,
              },
            });
          }
        }
        loadAssets();
      } else {
        actions.setState({
          sounds: gameData.sounds,
          images: gameData.images,
          sprites: gameData.sprites,
          isReady: true,
        });
      }
    }
  }, [gameData, gameAssetsFolder, prepareAssets, state.isReady]);

  return { state, actions };
}

async function preloadAudios(soundsData) {
  //console.log('Caching sounds...');

  let cachedSounds = null;
  try {
    const sounds = dedupSimple(Object.values(soundsData));
    const cachedSoundsArray = await Promise.all(sounds.map(preloadAudio));
    cachedSounds = cachedSoundsArray.reduce((acc, { id, audio }) => {
      acc[id] = { id, audio };
      return acc;
    }, {});
  } catch (error) {
    console.error('Error preloading sounds:', error);
    sendEvent({
      type: 'GAME_ASSETS_CONTEXT_PRELOAD_AUDIOS',
      data: {
        soundsData,
        error,
      },
      result: 'error',
    });
  }
  //console.log('Sounds preloaded:', soundsData);
  return cachedSounds || soundsData;
}

async function preloadImages(imagesData) {
  //console.log('Caching images...');
  try {
    const images = dedupSimple(
      Object.values(imagesData).map((image) => image.src)
    );

    await Promise.all(images.map(preloadImage));
  } catch (error) {
    console.error('Error preloading images:', error);
    sendEvent({
      type: 'GAME_ASSETS_CONTEXT_PRELOAD_IMAGES',
      data: {
        imagesData,
        error,
      },
      result: 'error',
    });
  }
  //console.log('Images preloaded:', imagesData);
  return imagesData;
}

async function preloadSprites(spritesData) {
  //console.log('Caching spritesheet images...');

  try {
    const spriteSheets = dedupSimple(
      Object.values(spritesData).map((sprite) => sprite.image)
    );
    await Promise.all(spriteSheets.map(preloadImage));
  } catch (error) {
    console.error('Error preloading sprites:', error);
    sendEvent({
      type: 'GAME_ASSETS_CONTEXT_PRELOAD_SPRITES',
      data: {
        spritesData: truncateObjectString(JSON.stringify(spritesData)),
        error,
      },
      result: 'error',
    });
  }
  //console.log('SpriteSheets preloaded:', spritesData);
  return spritesData;
}

function preloadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      resolve({ url, src: img.src });
    };
    img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    img.src = url;
  });
}

function preloadAudio({ id, audio }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', audio, true);
    xhr.responseType = 'blob';
    xhr.onload = () => {
      if (xhr.status === 200) {
        const blob = xhr.response;
        const audioURL = URL.createObjectURL(blob);
        resolve({
          id: id,
          audio: audioURL,
        });
      } else {
        reject(new Error(`Failed to load audio: ${audio}`));
      }
    };
    xhr.onerror = () =>
      reject(new Error(`Error while loading audio: ${audio}`));
    xhr.send();
  });
}
